import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { thenReturn } from '@app/shared/extra/utils';
import { AuthService } from '@auth0/auth0-angular';
import { AccessManager } from '@services/managers/access.manager';
import { Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Perform OAuth 2.0 authentication through redirection then fetch the authentication user's information from the API.
 * If the user is authenticated and their information could successfully be fetched, the route can be activated.
 * The user is always prompted for their credentials on login, even if they already have an Auth0 session from another
 * application (no seamless SSO into myA).
 * @param _route Activated route.
 * @param state Router state.
 * @return An Observable that will emit true once the user is authenticated or false otherwise.
 */
export default function authenticationGuard(_route: ActivatedRouteSnapshot,
                                            state: RouterStateSnapshot): Observable<boolean> {
  const authService = inject(AuthService);
  const accessManager = inject(AccessManager);
  const analyticsService = inject(AnalyticsService);

  return authService.isAuthenticated$
    .pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return authService.loginWithRedirect({
            appState: {target: state.url},
            authorizationParams: {prompt: 'login'}
          })
            .pipe(thenReturn(false));
        }
        if (accessManager.currentUser) return of(true);
        return accessManager.loadCurrentUser()
          .pipe(
            tap(user => analyticsService.setSessionInformation(user)),
            thenReturn(true)
          );
      })
    );
}
